"use client";

import { clsx } from "clsx";
import { SimpleVideoPlayer } from "@ui/components/core/SimpleSquareVideo";
import { getVideoUrl } from "@/app/_utilities/payload";
import type { Media } from "@mw/shared/payload-types";

export interface VideoConfig {
  type: "video";
  title?: string | null;
  description?: string | null;
  video?: (string | Media | null) | undefined;
  hasAudio?: boolean | null;
}

interface ChartVideoProps {
  video: VideoConfig;
  /**
   * Whether to show title and description caption
   */
  showCaption?: boolean;
  /**
   * Whether to include padding (default: true)
   */
  padding?: boolean;
  /**
   * Custom className for the container element
   */
  className?: string;
}

export const ChartVideo = ({ video, showCaption = true, padding = true, className }: ChartVideoProps) => {
  const { title, description, video: videoMedia, hasAudio } = video;

  // Get video URL - handle both string URLs and Media objects
  const videoUrl = typeof videoMedia === "string" ? videoMedia : getVideoUrl(videoMedia);

  if (!videoUrl) {
    return null;
  }

  return (
    <div className={clsx("flex flex-col", padding && "p-24 md:p-48", className)}>
      {/* Video container with same height as bar chart */}
      <div className={clsx("flex", padding && "mb-40 md:mb-56")}>
        <div className='relative flex w-full flex-1 flex-col pb-16'>
          {/* Video with fixed height matching bar chart */}
          <div className='relative mt-12 flex items-center justify-center md:h-[18.75rem]'>
            <SimpleVideoPlayer
              src={videoUrl}
              autoPlayWhenInView
              initialMuted
              showMuteButton={!!hasAudio}
              className='mb-0 aspect-3x2 w-full md:mx-auto md:h-[15rem] md:w-auto'
              fullWidth={false}
              breakoutMuteButton
              muteButtonVariant='elevated'
              fit='contain'
            />
          </div>
        </div>
      </div>

      {/* Title and description */}
      {showCaption && (
        <div>
          {title && <h3 className='f-heading-04'>{title}</h3>}
          {description && <p className='f-paragraph-02 mt-8 text-light'>{description}</p>}
        </div>
      )}
    </div>
  );
};
